import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { GrLinkTop } from "react-icons/gr";
import { PARAPHRASE_API } from '../apiConfig';
import './ParaphrasingTool.css';

// CSS references similar to plagiarism.js
import '../Css/BeamProperties.css';
import '../Css/BeamDeflection.css';
import '../Css/NumberLine.css';
import '../Css/AboutUS.css';
import '../Css/Navbar.css';
import '../Css/Padeye.css';

function ParaphrasingTool() {
  const [inputText, setInputText] = useState('');
  const [paraphrasedText, setParaphrasedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('standard');
  const [copyStatus, setCopyStatus] = useState('Copy');

  const [ballCount, setBallCount] = useState(0);
  const ballRefs = useRef([]);

  // Ball count calculator for background animation
  useEffect(() => {
    const spacingVH = 70;
    const calculateBallCount = () => {
      const pageHeight = document.documentElement.scrollHeight;
      const vh = window.innerHeight / 100;
      const spacingPx = spacingVH * vh;
      const count = Math.ceil(pageHeight / spacingPx);
      setBallCount(count);
    };

    calculateBallCount();
    window.addEventListener('resize', calculateBallCount);
    window.addEventListener('scroll', calculateBallCount);

    const observer = new MutationObserver(calculateBallCount);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    return () => {
      window.removeEventListener('resize', calculateBallCount);
      window.removeEventListener('scroll', calculateBallCount);
      observer.disconnect();
    };
  }, []);

  // Intersection observer for animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    ballRefs.current.forEach(ball => ball && observer.observe(ball));
    return () => observer.disconnect();
  }, [ballCount]);

  const getInputWordCount = () => {
    return inputText.trim() ? inputText.trim().split(/\s+/).length : 0;
  };

  const getOutputWordCount = () => {
    return paraphrasedText.trim() ? paraphrasedText.trim().split(/\s+/).length : 0;
  };

  const handleParaphrase = async () => {
    const trimmed = inputText.trim();
    if (!trimmed) return;

    const words = trimmed.split(/\s+/).length;
    if (words > 500) {
      alert('Free tier supports up to 500 words. Please shorten your text.');
      return;
    }

    setLoading(true);
    setParaphrasedText('');

    try {
      const response = await fetch(`${PARAPHRASE_API}/api/paraphrase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: trimmed, mode }),
      });

      const data = await response.json();
      if (data.paraphrased) {
        setParaphrasedText(data.paraphrased);
      } else if (data.error) {
        setParaphrasedText(`Error: ${data.error}`);
      }
    } catch (err) {
      setParaphrasedText('Error: Could not connect to the backend. Please try again in a few moments.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setInputText('');
    setParaphrasedText('');
    setCopyStatus('Copy');
  };

  const handleCopy = () => {
    if (!paraphrasedText) return;
    navigator.clipboard.writeText(paraphrasedText).then(() => {
      setCopyStatus('Copied!');
      setTimeout(() => setCopyStatus('Copy'), 2000);
    });
  };

  const handleRefine = () => {
    if (!paraphrasedText) return;
    setInputText(paraphrasedText);
    // Trigger paraphrase on next tick with the updated text
    setTimeout(() => {
      const btn = document.getElementById('rewriteBtn');
      if (btn) btn.click();
    }, 50);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <>
      <Helmet>
        <title>AI Paraphraser — Rewrite Content with Precision | OOK Calculator</title>
        <meta
          name="description"
          content="OOK's AI Paraphrasing Engine rewrites your content in Standard, Fluency, Creative, or Formal mode — preserving meaning while improving clarity, tone, and readability. Free, private, instant."
        />
        <meta name="keywords" content="AI paraphraser, paraphrasing tool, rewrite content, text rewriter, academic paraphraser, free paraphraser, OOK Calculator" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="AI Paraphraser — Rewrite Content with Precision | OOK Calculator" />
        <meta property="og:description" content="Four rewriting modes. Zero data retention. Results in under 3 seconds." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://ookcalculator.com/paraphraser" />

        {/* Structured schemas */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "OOK AI Paraphraser",
            "description": "Advanced neural paraphrasing engine with four writing modes. Preserves meaning while improving clarity, tone, and readability.",
            "applicationCategory": "UtilitiesApplication",
            "operatingSystem": "Any",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
          }
          `}
        </script>

        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How many words can I paraphrase at once?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The free tier supports up to 500 words (5,000 characters) per request. Sign in to unlock extended limits for longer documents and batch processing workflows."
                }
              },
              {
                "@type": "Question",
                "name": "Does the paraphraser change the meaning of my text?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No. The engine is explicitly trained to preserve semantic meaning while only modifying syntax, vocabulary, and sentence structure. Meaning fidelity is the primary optimisation target of the model."
                }
              },
              {
                "@type": "Question",
                "name": "Which mode should I use for academic writing?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Use Formal mode for academic papers and research reports. It elevates vocabulary to Latinate and technical registers and structures sentences for precision and clarity — at the cost of casual readability."
                }
              },
              {
                "@type": "Question",
                "name": "Can I paraphrase text in languages other than English?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The engine is currently optimised for English. Multilingual support for French, Spanish, German, and Hindi is on the product roadmap and will be available in a future release."
                }
              },
              {
                "@type": "Question",
                "name": "Is my text stored or used for AI training?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No. OOK operates a strict zero-retention policy. Input text is processed in memory and discarded immediately after the session ends. It is never logged, persisted, or used for model training purposes."
                }
              },
              {
                "@type": "Question",
                "name": "What does the Refine button do?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The Refine button runs the paraphrased output back through the engine as new input, allowing you to progressively improve clarity, tone, and style across multiple passes until you are satisfied with the result."
                }
              }
            ]
          }
          `}
        </script>

        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://ook-calculator.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "AI Paraphraser",
                "item": "https://ook-calculator.com/Paraphrase"
              }
            ]
          }
          `}
        </script>
      </Helmet>

      <div className="paraphraser-theme-wrapper">
        {/* BREADCRUMB */}
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <a href="https://ook-calculator.com">Home</a>
          <span aria-hidden="true">›</span>
          <span aria-current="page">AI Paraphraser</span>
        </nav>

        {/* HERO */}
        <header className="hero" role="banner">
          <div className="badge" aria-label="Tool category">Free Online Tool</div>
          <h1>
            Rewrite Content with<br />— <em>Forensic Precision</em>
          </h1>
          <p className="hero-sub">
            Advanced AI-powered rewriting that preserves original meaning while improving clarity, tone, and readability — engineered for professionals, students, and content creators.
          </p>
          <div className="trust-row" aria-label="Tool highlights">
            <span className="trust-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
              Standard, Fluency, Creative & Formal modes
            </span>
            <span className="trust-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
              Zero data retention
            </span>
            <span className="trust-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
              Fidelity semantic preservation
            </span>
          </div>
        </header>

        {/* BACKGROUND BALL ANIMATION */}
        <div className="ball-section">
          {Array.from({ length: ballCount }, (_, i) => (
            <div
              key={i}
              ref={el => (ballRefs.current[i] = el)}
              className={`ball ball-${(i % 10) + 1}`}
              style={{
                position: 'absolute',
                top: `${i * 70}vh`,
                left: i % 2 === 0 ? '0%' : '97%',
                transform: 'translateX(-50%)',
                animationDelay: `${Math.random() * 5}s`,
              }}
            />
          ))}
        </div>

        {/* TOOL WIDGET */}
        <main id="main-content">
          <section className="tool-section" aria-label="AI paraphrasing tool">
            <div className="mode-tabs" role="tablist" aria-label="Writing mode">
              {[
                { id: 'standard', label: 'Standard' },
                { id: 'fluency', label: '✦ Fluency' },
                { id: 'creative', label: '✳ Creative' },
                { id: 'formal', label: '🎓 Formal' }
              ].map(t => (
                <button
                  key={t.id}
                  className={`tab ${mode === t.id ? 'active' : ''}`}
                  role="tab"
                  aria-selected={mode === t.id}
                  onClick={() => setMode(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="editor-grid">
              {/* INPUT PANE */}
              <div className="editor-pane">
                <div className="pane-header">
                  <span><span className="icon">✏</span> &nbsp;INPUT</span>
                  <span className="word-count" id="wc">
                    {getInputWordCount()} / 500 words
                  </span>
                </div>
                <textarea
                  id="inputText"
                  placeholder="Paste or type your content here to begin paraphrasing…"
                  aria-label="Input text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                />
                <div className="pane-footer">
                  <button className="btn-sm" onClick={handleReset}>Clear</button>
                  <button
                    className="btn-sm primary"
                    id="rewriteBtn"
                    onClick={handleParaphrase}
                    disabled={loading || !inputText.trim()}
                  >
                    {loading ? (
                      <span className="loading-spinner"></span>
                    ) : (
                      'Rewrite →'
                    )}
                  </button>
                </div>
              </div>

              {/* OUTPUT PANE */}
              <div className="editor-pane">
                <div className="pane-header">
                  <span><span className="icon">⚡</span> &nbsp;OUTPUT</span>
                  <span className="word-count" id="owc">
                    {paraphrasedText ? `${getOutputWordCount()} words` : '—'}
                  </span>
                </div>
                <div className="output-area" id="outputArea" aria-live="polite">
                  {!paraphrasedText && !loading ? (
                    <div className="standby" id="standbyMsg">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                        <rect x="4" y="11" width="3" height="6" rx="1.5" fill="#ccc" />
                        <rect x="9.5" y="7" width="3" height="14" rx="1.5" fill="#ccc" />
                        <rect x="15" y="9" width="3" height="10" rx="1.5" fill="#ccc" />
                        <rect x="20.5" y="12" width="3" height="4" rx="1.5" fill="#ccc" />
                      </svg>
                      Neural engine on standby.<br />Awaiting input.
                    </div>
                  ) : (
                    paraphrasedText
                  )}
                </div>
                <div className="pane-footer">
                  <span></span>
                  <div className="pane-actions">
                    <button
                      className="btn-sm"
                      onClick={handleCopy}
                      disabled={!paraphrasedText || loading}
                    >
                      {copyStatus}
                    </button>
                    <button
                      className="btn-sm"
                      onClick={handleRefine}
                      disabled={!paraphrasedText || loading}
                    >
                      ↺ Refine
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <p style={{ fontSize: '.78rem', color: '#bbb', marginTop: '10px', textAlign: 'right' }}>
              Free tier: up to 500 words per request &nbsp;·&nbsp; Zero data retention &nbsp;·&nbsp; <a href="#tool" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Sign in</a> for extended limits
            </p>
          </section>

          {/* STATS */}
          <div className="stats-grid" role="list" aria-label="Usage statistics">
            <div className="stat-cell" role="listitem">
              <div className="stat-num">2M+</div>
              <div className="stat-label">Texts Rewritten</div>
            </div>
            <div className="stat-cell" role="listitem">
              <div className="stat-num">99.9%</div>
              <div className="stat-label">Meaning Retention</div>
            </div>
            <div className="stat-cell" role="listitem">
              <div className="stat-num">4 Modes</div>
              <div className="stat-label">Output Registers</div>
            </div>
            <div className="stat-cell" role="listitem">
              <div className="stat-num">&lt;5s</div>
              <div className="stat-label">Average Response Time</div>
            </div>
          </div>

          {/* WRITING MODES DESCRIPTION */}
          <section className="content-wrap section" id="modes">
            <p className="section-eyebrow">Rewriting Modes</p>
            <h2 className="section-title">Choose your <em>output register</em></h2>
            <div className="modes-grid">
              <div className="mode-card highlighted">
                <div className="mode-label">Standard</div>
                <h3>Balanced Rewrite</h3>
                <p>General-purpose paraphrasing that improves readability without shifting tone. Best for everyday content and blog posts.</p>
              </div>
              <div className="mode-card">
                <div className="mode-label">Fluency</div>
                <h3>Natural Flow</h3>
                <p>Optimised for native-sounding, conversational prose. Ideal for ESL writers seeking a human-like reading experience.</p>
              </div>
              <div className="mode-card">
                <div className="mode-label">Creative</div>
                <h3>Expressive Rewrite</h3>
                <p>Injects vivid language and varied sentence structures with rhetorical depth — perfect for marketing and storytelling.</p>
              </div>
              <div className="mode-card">
                <div className="mode-label">Formal</div>
                <h3>Academic Register</h3>
                <p>Elevates vocabulary and sentence complexity for research papers, legal documents, and professional reports.</p>
              </div>
            </div>
          </section>

          {/* FEATURES / CAPABILITIES */}
          <section className="content-wrap section" id="features">
            <p className="section-eyebrow">Capabilities</p>
            <h2 className="section-title">Built for <em>precision-grade</em><br />content rewriting</h2>
            <div className="features-grid">
              <article className="feature-card">
                <div className="feature-icon" aria-hidden="true">🧠</div>
                <div>
                  <h4>Semantic Preservation</h4>
                  <p>Our fine-tuned model restructures syntax and vocabulary while keeping the core meaning of your text fully intact.</p>
                </div>
              </article>
              <article className="feature-card">
                <div className="feature-icon" aria-hidden="true">🔒</div>
                <div>
                  <h4>Zero Data Retention</h4>
                  <p>Input text is processed in memory and discarded immediately after each session. Nothing is logged or used for training.</p>
                </div>
              </article>
              <article className="feature-card">
                <div className="feature-icon" aria-hidden="true">✅</div>
                <div>
                  <h4>Grammar-Safe Output</h4>
                  <p>Every rewrite is validated for grammatical integrity, punctuation consistency, and natural language flow before delivery.</p>
                </div>
              </article>
              <article className="feature-card">
                <div className="feature-icon" aria-hidden="true">↺</div>
                <div>
                  <h4>Iterative Refinement</h4>
                  <p>Use the Refine button to run output back through the engine, progressively improving tone, style, and precision.</p>
                </div>
              </article>
            </div>
          </section>

          {/* FAQ */}
          <section className="content-wrap section" id="faq">
            <p className="section-eyebrow">FAQ</p>
            <h2 className="section-title">Frequently asked <em>questions</em></h2>
            <div className="faq-list">
              <details>
                <summary>How many words can I paraphrase at once?</summary>
                <div className="faq-answer">The free tier supports up to 500 words (5,000 characters) per request. Sign in to unlock extended limits for longer documents and batch processing workflows.</div>
              </details>
              <details>
                <summary>Does the paraphraser change the meaning of my text?</summary>
                <div className="faq-answer">No. The engine is explicitly trained to preserve semantic meaning while only modifying syntax, vocabulary, and sentence structure. Meaning fidelity is the primary optimisation target of the model.</div>
              </details>
              <details>
                <summary>Which mode should I use for academic writing?</summary>
                <div className="faq-answer">Use Formal mode for academic papers and research reports. It elevates vocabulary to Latinate and technical registers and structures sentences for precision and clarity — at the cost of casual readability.</div>
              </details>
              <details>
                <summary>Can I paraphrase text in languages other than English?</summary>
                <div className="faq-answer">The engine is currently optimised for English. Multilingual support for French, Spanish, German, and Hindi is on the product roadmap and will be available in a future release.</div>
              </details>
              <details>
                <summary>Is my text stored or used for AI training?</summary>
                <div className="faq-answer">No. OOK operates a strict zero-retention policy. Input text is processed in memory and discarded immediately after the session ends. It is never logged, persisted, or used for model training purposes.</div>
              </details>
              <details>
                <summary>What does the Refine button do?</summary>
                <div className="faq-answer">The Refine button runs the paraphrased output back through the engine as new input, allowing you to progressively improve clarity, tone, and style across multiple passes until you are satisfied with the result.</div>
              </details>
            </div>
          </section>

          {/* RELATED TOOLS */}
          <section className="content-wrap section" aria-labelledby="related-tools-heading">
            <p className="section-eyebrow">Explore More Free Writing Tools</p>
            <h2 className="section-title">Explore more <em>free writing tools</em></h2>
            <nav className="related-grid" aria-label="Related tools">
              <a href="https://ook-calculator.com/AIDetector" className="related-card">
                <span aria-hidden="true">🤖</span>
                <span>AI Content Detector</span>
              </a>
              <a href="https://ook-calculator.com/Paraphrase" className="related-card">
                <span aria-hidden="true">✏️</span>
                <span>Paraphrase Tool</span>
              </a>
              <a href="https://ook-calculator.com/Humanize" className="related-card">
                <span aria-hidden="true">🧑</span>
                <span>AI Humanizer</span>
              </a>
              <a href="https://ook-calculator.com/GrammarChecker" className="related-card">
                <span aria-hidden="true">📝</span>
                <span>Grammar Checker</span>
              </a>
              <a href="https://ook-calculator.com/Summarizer" className="related-card">
                <span aria-hidden="true">📋</span>
                <span>Text Summarizer</span>
              </a>
              <a href="https://ook-calculator.com" className="related-card">
                <span aria-hidden="true">🧮</span>
                <span>All Tools</span>
              </a>
            </nav>
          </section>
        </main>

        {/* SCROLL TO TOP */}
        <section className="cse-header-top">
          <Link to="#" onClick={scrollToTop} aria-label="Scroll to top">
            <GrLinkTop />
          </Link>
        </section>
      </div>
    </>
  );
}

export default ParaphrasingTool;
